import React, { useEffect, useState } from 'react';

// CSS-only confetti celebration component
export const Confetti = ({ duration = 5000, onComplete }) => {
  const [particles, setParticles] = useState([]);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Generate confetti particles
    const colors = ['#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50', '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800', '#ff5722'];
    const shapes = ['square', 'circle', 'triangle'];
    
    const newParticles = Array.from({ length: 150 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: -10 - Math.random() * 20,
      size: 6 + Math.random() * 10,
      color: colors[Math.floor(Math.random() * colors.length)],
      shape: shapes[Math.floor(Math.random() * shapes.length)],
      rotation: Math.random() * 360,
      delay: Math.random() * 2,
      duration: 3 + Math.random() * 3,
      wobble: 20 + Math.random() * 30,
    }));
    
    setParticles(newParticles);

    // Auto-hide after duration
    const timer = setTimeout(() => {
      setVisible(false);
      onComplete?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onComplete]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden" data-testid="confetti-celebration">
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute animate-confetti-fall"
          style={{
            left: `${particle.left}%`,
            top: `${particle.top}%`,
            width: particle.size,
            height: particle.size,
            backgroundColor: particle.shape !== 'triangle' ? particle.color : 'transparent',
            borderRadius: particle.shape === 'circle' ? '50%' : '0',
            borderLeft: particle.shape === 'triangle' ? `${particle.size / 2}px solid transparent` : 'none',
            borderRight: particle.shape === 'triangle' ? `${particle.size / 2}px solid transparent` : 'none',
            borderBottom: particle.shape === 'triangle' ? `${particle.size}px solid ${particle.color}` : 'none',
            transform: `rotate(${particle.rotation}deg)`,
            animation: `confetti-fall ${particle.duration}s ease-in-out ${particle.delay}s forwards, confetti-wobble ${particle.duration / 2}s ease-in-out ${particle.delay}s infinite alternate`,
            '--wobble': `${particle.wobble}px`,
          }}
        />
      ))}
      
      <style>{`
        @keyframes confetti-fall {
          0% {
            top: -10%;
            opacity: 1;
          }
          100% {
            top: 110%;
            opacity: 0;
          }
        }
        
        @keyframes confetti-wobble {
          0% {
            transform: translateX(0) rotate(0deg);
          }
          100% {
            transform: translateX(var(--wobble, 30px)) rotate(360deg);
          }
        }
        
        .animate-confetti-fall {
          will-change: transform, top, opacity;
        }
      `}</style>
    </div>
  );
};

// Alternative: Flower rain effect
export const FlowerRain = ({ duration = 5000, onComplete }) => {
  const [petals, setPetals] = useState([]);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const petalColors = ['#ffb7c5', '#ffc0cb', '#ff69b4', '#ff1493', '#db7093', '#f8bbd9', '#e8b4d1'];
    
    const newPetals = Array.from({ length: 80 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: -10 - Math.random() * 20,
      size: 15 + Math.random() * 15,
      color: petalColors[Math.floor(Math.random() * petalColors.length)],
      delay: Math.random() * 3,
      duration: 4 + Math.random() * 4,
      swing: 30 + Math.random() * 50,
      rotation: Math.random() * 360,
    }));
    
    setPetals(newPetals);

    const timer = setTimeout(() => {
      setVisible(false);
      onComplete?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onComplete]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden" data-testid="flower-celebration">
      {petals.map((petal) => (
        <div
          key={petal.id}
          className="absolute"
          style={{
            left: `${petal.left}%`,
            top: `${petal.top}%`,
            width: petal.size,
            height: petal.size,
            background: `radial-gradient(ellipse at 30% 30%, ${petal.color}, transparent 70%)`,
            borderRadius: '50% 0 50% 50%',
            transform: `rotate(${petal.rotation}deg)`,
            animation: `petal-fall ${petal.duration}s ease-in-out ${petal.delay}s forwards, petal-swing ${petal.duration / 3}s ease-in-out ${petal.delay}s infinite alternate`,
            '--swing': `${petal.swing}px`,
            opacity: 0.9,
          }}
        />
      ))}
      
      <style>{`
        @keyframes petal-fall {
          0% {
            top: -10%;
            opacity: 1;
          }
          100% {
            top: 110%;
            opacity: 0.3;
          }
        }
        
        @keyframes petal-swing {
          0% {
            transform: translateX(calc(var(--swing, 30px) * -1)) rotate(0deg);
          }
          100% {
            transform: translateX(var(--swing, 30px)) rotate(45deg);
          }
        }
      `}</style>
    </div>
  );
};

// Paper flying celebration
export const PaperFly = ({ duration = 5000, onComplete }) => {
  const [papers, setPapers] = useState([]);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const colors = ['#fff', '#f0f0f0', '#e8e8e8', '#fafafa', '#fffacd', '#f0fff0', '#f0f8ff'];
    
    const newPapers = Array.from({ length: 60 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: -15 - Math.random() * 30,
      width: 20 + Math.random() * 20,
      height: 25 + Math.random() * 25,
      color: colors[Math.floor(Math.random() * colors.length)],
      delay: Math.random() * 2.5,
      duration: 5 + Math.random() * 4,
      swing: 40 + Math.random() * 60,
      flip: Math.random() > 0.5,
    }));
    
    setPapers(newPapers);

    const timer = setTimeout(() => {
      setVisible(false);
      onComplete?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onComplete]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden" data-testid="paper-celebration">
      {papers.map((paper) => (
        <div
          key={paper.id}
          className="absolute"
          style={{
            left: `${paper.left}%`,
            top: `${paper.top}%`,
            width: paper.width,
            height: paper.height,
            background: paper.color,
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            animation: `paper-fall ${paper.duration}s ease-in-out ${paper.delay}s forwards, paper-flip ${paper.duration / 4}s ease-in-out ${paper.delay}s infinite alternate`,
            '--swing': `${paper.swing}px`,
            transformStyle: 'preserve-3d',
          }}
        />
      ))}
      
      <style>{`
        @keyframes paper-fall {
          0% {
            top: -15%;
            opacity: 1;
          }
          100% {
            top: 115%;
            opacity: 0.5;
          }
        }
        
        @keyframes paper-flip {
          0% {
            transform: translateX(calc(var(--swing, 40px) * -1)) rotateX(0deg) rotateY(0deg);
          }
          100% {
            transform: translateX(var(--swing, 40px)) rotateX(180deg) rotateY(90deg);
          }
        }
      `}</style>
    </div>
  );
};

export default Confetti;
